import {
  Box,
  VStack,
  HStack,
  Flex,
  Text,
  Heading,
  Icon,
  Button,
  IconButton,
  Skeleton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  Badge,
  useBreakpointValue,
  SimpleGrid,
  Collapse,
  useDisclosure
} from '@chakra-ui/react';
import {
  ChatCircleDots,
  ChatCircle, 
  Clock, 
  FunnelSimple,
  CaretDown,
  CaretUp,
  Buildings,
  MapPin,
  User
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function RecentActivitySection({ recentActivity }) {
  const { isOpen, onToggle } = useDisclosure();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState('all'); // all, sites, replies
  const itemsPerPage = 6;
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const filteredComments = recentActivity.comments.filter(comment => {
    if (filter === 'all') return true;
    if (filter === 'sites') return comment.type === 'site';
    if (filter === 'replies') return comment.type === 'reply';
    return true;
  });

  const paginatedComments = filteredComments.slice(0, page * itemsPerPage);

  useEffect(() => {
    if (paginatedComments.length >= filteredComments.length) {
      setHasMore(false);
    } else {
      setHasMore(true);
    }
  }, [paginatedComments.length, filteredComments.length, filter]);

  const handleLoadMore = () => {
    setPage(prev => prev + 1);
  };

  const getFilterDisplayName = () => {
    switch (filter) {
      case 'sites': return 'site comments';
      case 'replies': return 'replies';
      default: return 'activities';
    }
  };

  return (
    <Box variant="dashboardSection" p={0}>
      <VStack align="stretch" spacing={0}>
        {/* Collapsible Header */}
        <Flex
          p={6}
          cursor="pointer"
          onClick={onToggle}
          _hover={{ bg: 'bg.accent' }}
          transition="background-color 0.2s"
          justify="space-between"
          align="center"
        >
          <HStack spacing={3}>
            <Flex
              w="40px"
              h="40px"
              bg="bg.accent"
              color="brand.secondary"
              borderRadius="md"
              justify="center"
              align="center"
            >
              <Icon as={ChatCircleDots} weight="duotone" fontSize="xl" />
            </Flex>
            <VStack align="start" spacing={1}>
              <Heading size="lg" fontFamily="heading" color="text.default">
                Recent Activity
              </Heading>
              <HStack spacing={2}>
                <Badge variant="subtle" colorScheme="gray">
                  {filteredComments.length} {getFilterDisplayName()}
                </Badge>
                <Text fontSize="sm" color="text.muted">
                  Click to {isOpen ? 'hide' : 'view'} your activity
                </Text>
              </HStack>
            </VStack>
          </HStack>
          
          <HStack spacing={3}>
            <Menu>
              <MenuButton
                as={Button}
                size="md"
                variant="journal"
                leftIcon={<FunnelSimple weight="bold" />}
                onClick={(e) => e.stopPropagation()}
              >
                {filter === 'all' ? 'All' : filter === 'sites' ? 'Sites' : 'Replies'}
              </MenuButton>
              <Portal>
                <MenuList bg="bg.surface" borderColor="border.default">
                  <MenuItem
                    onClick={() => setFilter('all')}
                    icon={<Icon as={ChatCircleDots} />}
                    _hover={{ bg: 'bg.accent' }}
                    _focus={{ bg: 'bg.accent' }}
                    bg="transparent"
                  >
                    All Activity
                  </MenuItem>
                  <MenuItem
                    onClick={() => setFilter('sites')}
                    icon={<Icon as={Buildings} />}
                    _hover={{ bg: 'bg.accent' }}
                    _focus={{ bg: 'bg.accent' }}
                    bg="transparent"
                  >
                    Site Comments
                  </MenuItem>
                  <MenuItem
                    onClick={() => setFilter('replies')}
                    icon={<Icon as={ChatCircle} />}
                    _hover={{ bg: 'bg.accent' }}
                    _focus={{ bg: 'bg.accent' }}
                    bg="transparent"
                  >
                    Replies
                  </MenuItem>
                </MenuList>
              </Portal>
            </Menu>
            <IconButton
              icon={<Icon as={isOpen ? CaretUp : CaretDown} weight="bold" />}
              variant="ghost"
              size="md"
              aria-label={isOpen ? "Hide activity" : "Show activity"}
              color="text.muted"
            />
          </HStack>
        </Flex>

        {/* Collapsible Content */}
        <Collapse in={isOpen} animateOpacity>
          <VStack align="stretch" spacing={6} px={6} pb={6}>
            {recentActivity.loading ? (
              <VStack spacing={4}>
                {[1, 2, 3].map((i) => (
                  <Skeleton
                    key={i}
                    height="120px"
                    width="100%"
                    borderRadius="md"
                    startColor="bg.surface"
                    endColor="bg.accent"
                  />
                ))}
              </VStack>
            ) : filteredComments.length === 0 ? (
              <Flex
                direction="column"
                align="center"
                justify="center"
                py={12}
                bg="bg.accent"
                borderRadius="md"
              >
                <Icon as={ChatCircle} fontSize="4xl" color="text.muted" mb={4} />
                <Heading size="md" color="text.default" mb={2}>
                  No {getFilterDisplayName()} yet
                </Heading>
                <Text color="text.muted" textAlign="center">
                  {filter === 'all' 
                    ? "Your comments and discussions will appear here"
                    : `Your ${getFilterDisplayName()} will appear here`
                  }
                </Text>
              </Flex>
            ) : (
              <>
                {/* Activity Cards Grid */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {paginatedComments.map((comment) => (
                    <Box
                      key={comment.id}
                      p={4}
                      borderWidth="1px"
                      borderRadius="md"
                      _hover={{
                        borderColor: "brand.secondary",
                        transform: 'translateY(-1px)',
                        boxShadow: 'md'
                      }}
                      transition="all 0.2s"
                      cursor="pointer"
                      bg="bg.surface"
                      borderColor="border.light"
                      onClick={() => navigate(`/site/${comment.siteId}#comment-${comment.id}`)}
                    >
                      <VStack align="start" spacing={3}>
                        <HStack spacing={3} w="full">
                          <Flex
                            w="32px"
                            h="32px"
                            bg={comment.type === 'reply' ? 'green.100' : 'blue.100'}
                            color={comment.type === 'reply' ? 'green.600' : 'blue.600'}
                            borderRadius="full"
                            justify="center"
                            align="center"
                            flexShrink={0}
                          >
                            <Icon as={comment.type === 'reply' ? User : MapPin} weight="fill" fontSize="md" />
                          </Flex>
                          <VStack align="start" spacing={1} flex={1}>
                            <Badge
                              variant="subtle"
                              colorScheme={comment.type === 'reply' ? 'green' : 'blue'}
                              fontSize="xs"
                            >
                              {comment.type === 'reply' ? 'Reply' : 'Site Comment'}
                            </Badge>
                            {comment.siteName && (
                              <Text fontSize="sm" color="text.muted" fontWeight="medium">
                                {comment.siteName}
                              </Text>
                            )}
                          </VStack>
                        </HStack>
                        
                        <Text fontSize="sm" noOfLines={3} color="text.default" fontStyle="italic">
                          "{comment.text.length > 120 ?
                            `${comment.text.substring(0, 120)}...` :
                            comment.text}"
                        </Text>
                        
                        <HStack w="full" justify="space-between" align="center">
                          <HStack spacing={2}>
                            <Icon as={Clock} fontSize="xs" color="text.muted" />
                            <Text fontSize="xs" color="text.muted">
                              {comment.createdAt?.seconds ?
                                new Date(comment.createdAt.seconds * 1000).toLocaleDateString() :
                                new Date().toLocaleDateString()}
                            </Text>
                          </HStack>
                        </HStack>
                      </VStack>
                    </Box>
                  ))}
                </SimpleGrid>

                {/* Load More Button */}
                {hasMore && (
                  <Flex justify="center" mt={4}>
                    <Button
                      variant="journal"
                      size="md"
                      onClick={handleLoadMore}
                      isLoading={recentActivity.loading}
                    >
                      Load More Activity
                    </Button>
                  </Flex>
                )}
              </>
            )}
          </VStack>
        </Collapse>
      </VStack>
    </Box>
  );
}
